// Shaping the Organization snapshot (Regions / Dealers / Workshops /
// People) into rows. Regions group dealers, dealers own workshops, workshops
// hold the people who talk to customers.
//
// Pure functions only — everything here is derived from one
// GET /superadmin/tenants/{id}/organization payload so the three pages agree
// with each other. The API decides what a person may actually see; this only
// says it in words.

import { PERMISSIONS, READ_SUFFIX, levelOf, policyLabel } from "./access";

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

export const EMPTY_ORG = { regions: [], dealers: [], workshops: [], people: [], roles: [] };

export function indexOrg(org) {
  const o = org || EMPTY_ORG;
  return {
    ...EMPTY_ORG,
    ...o,
    regionById: new Map((o.regions || []).map((r) => [r.id, r])),
    dealerById: new Map((o.dealers || []).map((d) => [d.id, d])),
    workshopById: new Map((o.workshops || []).map((w) => [w.id, w])),
    roleByCode: new Map((o.roles || []).map((r) => [r.code, r])),
  };
}

export function regionNamesForDealer(dealer, ix) {
  return (dealer?.region_ids || [])
    .map((id) => ix.regionById.get(id)?.name)
    .filter(Boolean)
    .sort();
}

export function dealersInRegion(regionId, ix) {
  return (ix.dealers || []).filter((d) => (d.region_ids || []).includes(regionId));
}

export function workshopsForDealer(dealerId, ix) {
  return (ix.workshops || []).filter((w) => w.dealer_id === dealerId);
}

/** Logins whose access is pinned to this one region — they are what stops
 *  the region being deleted. Mirrors list_app_users_scoped_to_region in
 *  apps/api/db.py, which is what actually enforces it. */
export function peopleScopedToRegion(regionId, ix) {
  return (ix.people || []).filter(
    (p) => p.has_login !== false && p.scope_level === "region" && p.scope_id === regionId,
  );
}

export function peopleAtWorkshop(location, ix) {
  const target = lower(location);
  return (ix.people || []).filter((p) => lower(p.workshop_location) === target);
}

/** One workshop row for the Workshops table: its dealer, its regions and how
 *  many logins sit there. */
export function workshopRow(workshop, ix) {
  const dealer = workshop.dealer_id ? ix.dealerById.get(workshop.dealer_id) : null;
  const regions = dealer ? regionNamesForDealer(dealer, ix) : [];
  return {
    id: workshop.id,
    location: norm(workshop.workshop_location),
    dealer: dealer ? dealer.name : "",
    region: regions.join(", "),
    users: workshop.user_count || 0,
    status: workshop.status === "paused" ? "paused" : "active",
  };
}

export function workshopRows(ix) {
  return (ix.workshops || []).map((w) => workshopRow(w, ix));
}

/** Where a login's access actually lands, in the brand's own words. The role
 *  carries the policy; the person carries which regions, dealers or workshops
 *  it resolves to. */
export function accessSummary(person, ix) {
  // Someone with no login cannot reach anything, so naming a scope for them
  // would describe access they do not have.
  if (person.has_login === false) return "—";
  const role = ix.roleByCode.get(person.role);
  const policy = role?.access_policy || "";
  const ids = person.scope_ids || [];
  const nameOf = (map, id) => map.get(id)?.name || "";

  if (policy === "entire_brand") return "Entire brand";
  if (policy === "one_region") return nameOf(ix.regionById, person.scope_id) || "No region set";
  if (policy === "one_workshop" || policy === "assigned_customers") {
    const at = norm(person.workshop_location);
    const suffix = policy === "assigned_customers" ? " · own customers" : "";
    return at ? at + suffix : "No workshop set";
  }
  const named = {
    selected_regions: () => ids.map((id) => nameOf(ix.regionById, id)),
    selected_dealers: () => ids.map((id) => ix.dealerById.get(id)?.name || ""),
    selected_workshops: () => ids.map((id) => ix.workshopById.get(id)?.workshop_location || ""),
  }[policy];
  if (!named) return policyLabel(policy);

  const names = named().filter(Boolean).sort();
  if (!names.length) return "Nothing selected";
  if (names.length <= 2) return names.join(", ");
  return `${names[0]}, ${names[1]} +${names.length - 2}`;
}

/** The part of the hierarchy a person sits in, for the People table's
 *  Organization column. */
export function organizationLabel(person, ix) {
  const at = norm(person.workshop_location);
  if (!at) return "—";
  const workshop = (ix.workshops || []).find((w) => lower(w.workshop_location) === lower(at));
  const dealer = workshop?.dealer_id ? ix.dealerById.get(workshop.dealer_id) : null;
  return dealer ? `${dealer.name} · ${at}` : at;
}

/** A role code the brand has not defined as a row — "master" on most brands
 *  — would otherwise render as the raw code. */
function prettyRoleCode(code) {
  if (code === "master") return "Brand Admin";
  return norm(code)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export function personRow(person, ix) {
  const role = ix.roleByCode.get(person.role);
  const at = norm(person.workshop_location);
  const workshop = at
    ? (ix.workshops || []).find((w) => lower(w.workshop_location) === lower(at))
    : null;
  const dealer = workshop?.dealer_id ? ix.dealerById.get(workshop.dealer_id) : null;
  return {
    id: person.id,
    // Only a login can be deleted here; a stakeholder or advisor is managed
    // on the Workshop page, which owns their contact details.
    userId: person.user_id ?? null,
    hasLogin: person.has_login !== false,
    // Where they sit, for the dealer and workshop filters. Someone scoped to
    // a region or a set of dealers works at no single workshop, so both stay
    // empty rather than guessing one for them.
    workshopLocation: at,
    dealerName: dealer?.name || "",
    name: norm(person.name) || norm(person.email).split("@")[0],
    email: norm(person.email),
    number: norm(person.number),
    roleLabel: role?.label || prettyRoleCode(person.role),
    org: organizationLabel(person, ix),
    access: accessSummary(person, ix),
    status: norm(person.status) || "invited",
  };
}

export function personRows(ix) {
  return (ix.people || []).map((p) => personRow(p, ix));
}

/** Region rows for the flat Regions page: what hangs off each region. */
export function regionRows(ix) {
  return (ix.regions || []).map((r) => {
    const dealers = dealersInRegion(r.id, ix);
    const workshops = dealers.flatMap((d) => workshopsForDealer(d.id, ix));
    return {
      id: r.id,
      name: r.name,
      dealers: dealers.length,
      workshops: workshops.length,
      people: workshops.reduce((n, w) => n + (w.user_count || 0), 0),
    };
  });
}

/** Dealer rows for the flat Dealers page. A dealer can serve more than one
 *  region, so regions is a list, not a value. */
export function dealerRows(ix) {
  return (ix.dealers || []).map((d) => {
    const workshops = workshopsForDealer(d.id, ix);
    return {
      id: d.id,
      name: d.name,
      code: d.dealer_code || "",
      regionIds: d.region_ids || [],
      regions: regionNamesForDealer(d, ix).join(", "),
      workshops: workshops.length,
      people: workshops.reduce((n, w) => n + (w.user_count || 0), 0),
    };
  });
}

/** Access policies that work above a single workshop, so a dealer filter is
 *  part of the job rather than clutter. Mirrors ACCESS_POLICIES in
 *  apps/api/schema_map.py. */
export const DEALER_LEVEL_POLICIES = new Set([
  "entire_brand",
  "selected_regions",
  "one_region",
  "selected_dealers",
]);
export const REGION_LEVEL_POLICIES = new Set(["entire_brand", "selected_regions", "one_region"]);

/** Regions a login can see, each with the workshops under it. A region's
 *  workshops come through its dealers. */
export function regionsInScope(ix, scopeLocations) {
  const inScope =
    scopeLocations === "all" ? null : new Set((scopeLocations || []).map((l) => lower(l)));
  return (ix.regions || [])
    .map((r) => {
      const dealerIds = new Set(dealersInRegion(r.id, ix).map((d) => d.id));
      const locations = (ix.workshops || [])
        .filter((w) => dealerIds.has(w.dealer_id))
        .map((w) => norm(w.workshop_location))
        .filter((l) => !inScope || inScope.has(lower(l)));
      return { id: r.id, name: r.name, locations };
    })
    .filter((r) => r.locations.length)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Dealers whose workshops a login can actually see, each with those
 *  workshops. A dealer with no workshop in scope is left out: selecting it
 *  would send an empty location list, which the API reads as no filter. */
export function dealersInScope(ix, scopeLocations) {
  const inScope =
    scopeLocations === "all"
      ? null
      : new Set((scopeLocations || []).map((l) => lower(l)));
  return (ix.dealers || [])
    .map((d) => {
      const locations = (ix.workshops || [])
        .filter((w) => w.dealer_id === d.id)
        .map((w) => norm(w.workshop_location))
        .filter((l) => !inScope || inScope.has(lower(l)));
      return { id: d.id, name: d.name, locations };
    })
    .filter((d) => d.locations.length)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Which workshops an analytics request should be narrowed to.
 *
 *  Precedence: one chosen workshop, else the chosen dealer, else the chosen
 *  region, else the login's whole scope. Undefined means "send nothing", which the API
 *  reads as the caller's full reach — only ever right for a login that can
 *  see the whole brand.
 */
export function analyticsLocations({
  workshop,
  allWorkshopsToken,
  dealerLocations,
  regionLocations,
  scopeLocations,
}) {
  if (workshop && workshop !== allWorkshopsToken) return workshop;
  if (dealerLocations) return dealerLocations.join(",");
  if (regionLocations) return regionLocations.join(",");
  if (scopeLocations !== "all" && (scopeLocations || []).length) return scopeLocations.join(",");
  return undefined;
}

export function filterRegions(rows, { query = "" } = {}) {
  const q = lower(query);
  return rows.filter((r) => !q || lower(r.name).includes(q));
}

export function filterDealers(rows, { query = "", region = "all" } = {}) {
  const q = lower(query);
  return rows.filter((r) => {
    if (q && !lower(r.name).includes(q) && !lower(r.code).includes(q)) return false;
    if (region !== "all" && !lower(r.regions).split(", ").includes(lower(region))) return false;
    return true;
  });
}

export function filterWorkshops(rows, { query = "", region = "all", dealer = "all", status = "all" } = {}) {
  const q = lower(query);
  return rows.filter((r) => {
    if (q && !lower(r.location).includes(q) && !lower(r.dealer).includes(q)) return false;
    if (region !== "all" && !lower(r.region).split(", ").includes(lower(region))) return false;
    if (dealer !== "all" && lower(r.dealer) !== lower(dealer)) return false;
    if (status !== "all" && r.status !== status) return false;
    return true;
  });
}

export function filterPeople(
  rows,
  { query = "", role = "all", status = "all", dealer = "all", workshop = "all" } = {},
) {
  const q = lower(query);
  return rows.filter((r) => {
    if (q && !lower(r.name).includes(q) && !lower(r.email).includes(q)) return false;
    if (role !== "all" && r.roleLabel !== role) return false;
    if (status !== "all" && r.status !== status) return false;
    if (dealer !== "all" && r.dealerName !== dealer) return false;
    if (workshop !== "all" && r.workshopLocation !== workshop) return false;
    return true;
  });
}

/** What deleting something takes with it, said plainly before it happens. */
export function deletionImpact(kind, id, ix) {
  if (kind === "regions") {
    const region = ix.regionById.get(id);
    const dealers = dealersInRegion(id, ix);
    // Logins scoped to this region lose their reach with it, so the API
    // refuses. Say so here rather than offering a Delete that 409s.
    const scoped = peopleScopedToRegion(id, ix);
    const who = scoped.map((p) => norm(p.email) || norm(p.name)).filter(Boolean);
    return {
      name: region?.name || "this region",
      blocked: dealers.length
        ? `${dealers.length} dealer${dealers.length === 1 ? " still sits" : "s still sit"} here. Move ${
            dealers.length === 1 ? "it" : "them"
          } first.`
        : who.length
          ? `${who.join(", ")} ${who.length === 1 ? "covers" : "cover"} this region. Change ${
              who.length === 1 ? "their" : "their"
            } region in People first.`
          : "",
    };
  }
  if (kind === "dealers") {
    const dealer = ix.dealerById.get(id);
    const workshops = workshopsForDealer(id, ix);
    return {
      name: dealer?.name || "this dealer",
      blocked: workshops.length
        ? `${workshops.length} workshop${workshops.length === 1 ? " belongs" : "s belong"} here. Move or delete ${
            workshops.length === 1 ? "it" : "them"
          } first.`
        : "",
    };
  }
  if (kind === "workshops") {
    const workshop = ix.workshopById.get(id);
    const heads = workshop ? peopleAtWorkshop(workshop.workshop_location, ix).length : 0;
    return {
      name: workshop?.workshop_location || "this workshop",
      blocked: "",
      note: heads
        ? `${heads} ${heads === 1 ? "person keeps their login but loses" : "people keep their logins but lose"} this posting.`
        : "",
    };
  }
  const person = (ix.people || []).find((p) => p.id === id);
  return { name: norm(person?.name) || norm(person?.email) || "this person", blocked: "" };
}

export function scopeQuestion(policy) {
  if (policy === "one_workshop" || policy === "assigned_customers") return "workshop";
  if (policy === "one_region") return "region";
  if (policy === "selected_regions") return "regions";
  if (policy === "selected_dealers") return "dealers";
  if (policy === "selected_workshops") return "workshops";
  return null;
}

// What question the form asks about where someone works. Adding from inside a
// workshop with no email is a contact at that workshop, whatever the role's
// policy: a General Manager's login may cover several workshops, but the
// workshop still needs its own GM contact (the Escalation Metrics message reads
// it). Give an email and it is a login again, scoped the way the role says.
export function whereQuestion({ policy, noLogin, atWorkshop, person, email }) {
  if (noLogin) return "workshop";
  if (atWorkshop && !person && !String(email || "").trim()) return "workshop";
  return scopeQuestion(policy);
}

// Roles a workshop is expected to have filled (advisors are many, so not listed).
export const WORKSHOP_ROLE_KEYS = ["gm", "wm", "ccm", "psf", "receptionist"];

/** The expected roles nobody at this workshop holds yet — only roles this
 *  brand actually has. A brand with no role list yet expects the usual five. */
export function unfilledRoles(location, ix) {
  const held = new Set(peopleAtWorkshop(location, ix).map((p) => p.role));
  const brandHas = (k) => !ix.roleByCode?.size || ix.roleByCode.has(k);
  return WORKSHOP_ROLE_KEYS.filter((k) => brandHas(k) && !held.has(k));
}

/** A role's name as the brand set it in this snapshot, else whatever
 *  `fallback` makes of the code. */
export const snapshotRoleLabel = (ix, code, fallback) => ix.roleByCode.get(code)?.label || fallback(code);

/** What removing someone takes with it, for the confirmation. */
export const personRemovalNote = (hasLogin) =>
  hasLogin
    ? "Their login stops working. Their past conversations stay where they are."
    : "They are removed from this workshop's contacts. Their past conversations stay where they are.";

// ---- form rules, shared by Super Admin and the brand's own pages ----------

export function regionFormErrors(name, ix, editingId) {
  const trimmed = norm(name);
  if (!trimmed) return { name: "Give the region a name." };
  const clash = ix.regions.some(
    (r) => r.id !== editingId && r.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  return clash ? { name: "A region with this name already exists." } : {};
}

export function dealerFormErrors(form, ix, editingId) {
  const out = {};
  const name = form.name.trim();
  const code = form.dealer_code.trim();
  if (!name) out.name = "Give the dealer a name.";
  if (!code) out.dealer_code = "A dealer code is required.";
  else if (ix.dealers.some((d) => d.id !== editingId && (d.dealer_code || "").toLowerCase() === code.toLowerCase()))
    out.dealer_code = "This code is already in use.";
  return out;
}

/** One problem at a time, in the order the form asks. */
export function workshopFormErrors(form, ix) {
  const name = form.workshop_location.trim();
  if (!name) return { workshop_location: "Give the workshop a name." };
  if (ix.workshops.some((w) => w.workshop_location.trim().toLowerCase() === name.toLowerCase()))
    return { workshop_location: "A workshop with this name already exists." };
  if (!form.dealer_id) return { dealer_id: "Pick the dealer this workshop belongs to." };
  return {};
}

export const BLANK_PERSON = {
  email: "",
  name: "",
  number: "",
  role: "",
  workshop_location: "",
  scope_id: "",
  scope_ids: [],
};

/** What the person form starts from: the person being edited, or a blank one
 *  on the first role (and at `atWorkshop` when adding from inside a workshop). */
export function personFormValues(person, ix, atWorkshop) {
  return person
    ? {
        email: person.email || "",
        name: person.name || "",
        number: person.number || "",
        role: person.role || "",
        workshop_location: person.workshop_location || "",
        scope_id: person.scope_id ? String(person.scope_id) : "",
        scope_ids: person.scope_ids || [],
      }
    : { ...BLANK_PERSON, role: ix.roles[0]?.code || "", workshop_location: atWorkshop || "" };
}

/** What the person form asks, given who is being edited and the role picked. */
export function personFormMode({ form, person, atWorkshop, ix }) {
  const role = ix.roleByCode.get(form.role);
  // A contact row has no account, so no policy applies to it — it simply
  // belongs to a workshop.
  const noLogin = !!person && person.has_login === false;
  const question = whereQuestion({ policy: role?.access_policy, noLogin, atWorkshop, person, email: form.email });
  return {
    role,
    noLogin,
    question,
    // Pinned to one workshop, so there is nothing to ask.
    askWorkshop: question === "workshop" && !atWorkshop,
    // Someone posted at one workshop can be added with just a name and number —
    // an email is what gives them a login, and most workshop staff have none.
    // Anyone whose role reaches several workshops needs the account.
    emailOptional: noLogin || question === "workshop" || (!!atWorkshop && !person),
    // A service advisor's role is what the row is, not a field on it.
    roleIsFixed: noLogin && person.role === "sa",
  };
}

const MANY = ["regions", "dealers", "workshops"];

export function personFormErrors(form, { question, emailOptional }, atWorkshop) {
  const out = {};
  const email = form.email.trim().toLowerCase();
  // A contact row needs no email — most workshop staff have none, which is
  // why they have no login. One that is typed still has to be an address,
  // since it is what their account would be created under.
  if (emailOptional ? email && !email.includes("@") : !email.includes("@")) {
    out.email = emailOptional
      ? "That doesn't look like an email address."
      : "A valid email address is required.";
  }
  // Optional, like a stakeholder's number — but a number that is there and
  // wrong is worse than none, since escalations would be sent into a void.
  const digits = form.number.replace(/\D/g, "");
  if (form.number.trim() && digits.length < 10) out.number = "That doesn't look like a phone number.";
  if (!form.role) out.role = "Pick a role.";
  const location = atWorkshop || form.workshop_location;
  if (question === "workshop" && !location) out.scope = "Pick the workshop they work at.";
  if (question === "region" && !form.scope_id) out.scope = "Pick the region they cover.";
  if (MANY.includes(question) && !form.scope_ids.length) out.scope = "Pick at least one.";
  return out;
}

/** The body of the write for one person (without `kind` or the id). */
export function personPayload(form, question, atWorkshop) {
  return {
    email: form.email.trim().toLowerCase(),
    name: form.name.trim(),
    number: form.number.trim(),
    role: form.role,
    workshop_location: question === "workshop" ? atWorkshop || form.workshop_location : null,
    scope_id: question === "region" ? Number(form.scope_id) : null,
    scope_ids: MANY.includes(question) ? form.scope_ids : [],
  };
}

// ---- the one-page Organization view ---------------------------------------

export const NO_REGION = "none";
const SEE_ALL = { regions: true, dealers: true, workshops: true };

/** Regions > dealers > workshops as one tree, after the page's filters.
 *
 *  `see` is which of the three pages the login may open: a part it may not
 *  open is neither listed nor searched, so the merged page shows nobody more
 *  than the three separate pages did. A dealer serving two regions sits under
 *  both; dealers in no region the login can see, and workshops with no dealer,
 *  collect under a last "Not in a region" group.
 */
export function organizationTree(
  ix,
  { query = "", region = "all", dealer = "all", status = "all" } = {},
  see = SEE_ALL,
) {
  const q = lower(query);
  const hit = (v) => lower(v).includes(q);
  const anyStatus = status === "all" || !see.workshops;
  const listDealers = see.dealers || see.workshops;
  const headCount = (shops) => shops.reduce((n, w) => n + (w.user_count || 0), 0);

  const shopRows = (shops, all) =>
    !see.workshops
      ? []
      : shops
          .map((w) => ({ ...workshopRow(w, ix), unfilled: unfilledRoles(w.workshop_location, ix).length }))
          .filter((w) => (anyStatus || w.status === status) && (all || hit(w.location)));

  const unplaced = (ix.dealers || []).filter((d) => !regionNamesForDealer(d, ix).length);
  const orphans = (ix.workshops || []).filter((w) => !w.dealer_id || !ix.dealerById.has(w.dealer_id));
  const groups = (ix.regions || []).map((r) => ({
    id: r.id,
    name: r.name,
    none: false,
    dealers: dealersInRegion(r.id, ix),
    orphans: [],
  }));
  if (unplaced.length || orphans.length)
    groups.push({ id: NO_REGION, name: "Not in a region", none: true, dealers: unplaced, orphans });

  return groups
    .filter((g) => region === "all" || String(g.id) === String(region))
    .map((g) => {
      const regionHit = !q || (see.regions && !g.none && hit(g.name));
      const dealers = !listDealers
        ? []
        : g.dealers
            .filter((d) => dealer === "all" || String(d.id) === String(dealer))
            .map((d) => {
              const own = workshopsForDealer(d.id, ix);
              const dealerHit = regionHit || hit(d.name) || (see.dealers && hit(d.dealer_code));
              const workshops = shopRows(own, dealerHit);
              return {
                id: d.id,
                name: d.name,
                code: d.dealer_code || "",
                otherRegions: regionNamesForDealer(d, ix).filter((n) => n !== g.name),
                workshopCount: own.length,
                workshops,
                // An empty dealer is a Dealers-page row, not a Workshops one.
                keep: workshops.length > 0 || (see.dealers && dealerHit && anyStatus),
              };
            })
            .filter((d) => d.keep);
      // Workshops nobody has placed under a dealer yet.
      const loose = dealer === "all" ? shopRows(g.orphans, regionHit) : [];
      if (loose.length)
        dealers.push({
          id: null,
          name: "No dealer",
          code: "",
          otherRegions: [],
          workshopCount: g.orphans.length,
          workshops: loose,
        });

      const shops = [...g.dealers.flatMap((d) => workshopsForDealer(d.id, ix)), ...g.orphans];
      return {
        id: g.id,
        name: g.name,
        none: g.none,
        stats: { dealers: g.dealers.length, workshops: shops.length, people: headCount(shops) },
        dealers,
        keep: dealers.length > 0 || (see.regions && !g.none && regionHit && dealer === "all" && anyStatus),
      };
    })
    .filter((g) => g.keep);
}

// ---- roles ----------------------------------------------------------------

export const KNOWN_PERMISSIONS = new Set(
  PERMISSIONS.flatMap((p) => (p.levelled ? [p.id, p.id + READ_SUFFIX] : [p.id])),
);

// Ungrouped first, then each group in the order it first appears.
export const PERMISSION_GROUPS = PERMISSIONS.reduce((groups, p) => {
  const label = p.group || "";
  const existing = groups.find((g) => g.label === label);
  if (existing) existing.items.push(p);
  else groups.push({ label, items: [p] });
  return groups;
}, []);

/** A role code from its name, until someone types one of their own. */
export const roleCodeFrom = (text) =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);

export const emptyRole = () => ({
  id: null,
  label: "",
  short_label: "",
  code: "",
  access_policy: "one_workshop",
  permissions: ["chat_audit"],
  is_system: false,
  user_count: 0,
});

/** The permissions this app knows how to show; anything else is dropped. */
export const knownPermissions = (role) => (role.permissions || []).filter((p) => KNOWN_PERMISSIONS.has(p));

export function roleFormErrors(form) {
  const errors = {};
  if (!form.label.trim()) errors.label = "Enter a role name.";
  if (!form.short_label.trim()) errors.short_label = "Enter a short name.";
  if (!/^[a-z][a-z0-9_]{1,31}$/.test(form.code)) errors.code = "Use 2–32 lowercase letters, numbers or underscores.";
  if (!form.permissions.length) errors.permissions = "Pick at least one permission.";
  return errors;
}

/** The body of a role write. */
export const rolePayload = (form) => ({
  id: form.id,
  code: form.code,
  label: form.label.trim(),
  short_label: form.short_label.trim(),
  access_policy: form.access_policy,
  permissions: form.permissions,
});

export const togglePermission = (permissions, id) =>
  permissions.includes(id) ? permissions.filter((p) => p !== id) : [...permissions, id];

// Edit implies read, so the two levels are mutually exclusive: setting one
// clears the other rather than leaving a role holding both.
export function withLevel(permissions, id, level) {
  const without = permissions.filter((p) => p !== id && p !== id + READ_SUFFIX);
  if (!level) return without;
  return [...without, level === "edit" ? id : id + READ_SUFFIX];
}

/** People rows in cards, one per workshop in the snapshot's order, with
 *  everyone who works at no single workshop last. */
export function groupPeopleByWorkshop(rows, ix) {
  const order = (ix.workshops || []).map((w) => norm(w.workshop_location));
  const byPlace = new Map();
  for (const r of rows) {
    const at = r.workshopLocation || "";
    if (!byPlace.has(at)) byPlace.set(at, []);
    byPlace.get(at).push(r);
  }
  const known = order.filter((l) => byPlace.has(l));
  const rest = [...byPlace.keys()].filter((l) => l && !order.includes(l)).sort();
  const groups = [...known, ...rest].map((l) => {
    const people = byPlace.get(l);
    return { key: l, name: l, dealer: people[0].dealerName, people };
  });
  if (byPlace.has("")) groups.push({ key: "", name: "", dealer: "", people: byPlace.get("") });
  return groups;
}
